// how long to wait for the element in the hash to be rendered, and how long to hold it in place
// afterwards while the rest of the page (images above it) loads in
const FIND_ELEMENT_TIMEOUT = 5000;
const HOLD_POSITION_TIMEOUT = 3000;

// Scroll to the element in the hash - on the timeline that is a section or an object, on page load
// and on the way back from an object or the glossary. The timeline only renders once isReady flips
// and its images load in afterwards, so one delayed scrollIntoView is not enough: the element can
// still be missing (no scroll at all), and content loading in above it drifts it back out of view
// (you end up at the section instead of the object). So wait for the element, then hold it in place
// until the layout settles. Returns a cancel function.
export const scrollToHash = (id: string) => {
    let cancelled = false;
    const timeouts: ReturnType<typeof setTimeout>[] = [];

    const cancel = () => {
        cancelled = true;
        timeouts.forEach(clearTimeout);
        window.removeEventListener('wheel', cancel);
        window.removeEventListener('touchmove', cancel);
        window.removeEventListener('keydown', cancel);
    };

    // scrolling yourself always wins (these don't fire for programmatic scrolling)
    window.addEventListener('wheel', cancel, { passive: true, once: true });
    window.addEventListener('touchmove', cancel, { passive: true, once: true });
    window.addEventListener('keydown', cancel, { once: true });

    // re-scroll while the element keeps moving down the page
    const holdInView = (el: HTMLElement, until: number) => {
        let lastTop: number | null = null;

        const check = () => {
            if (cancelled) return;
            const top = Math.round(el.getBoundingClientRect().top + window.scrollY);
            if (top !== lastTop) {
                el.scrollIntoView();
                lastTop = top;
            }
            if (Date.now() < until) timeouts.push(setTimeout(check, 250));
        };

        check();
    };

    const findElement = (until: number) => {
        if (cancelled) return;
        const el = document.getElementById(id);
        if (el) {
            holdInView(el, Date.now() + HOLD_POSITION_TIMEOUT);
        } else if (Date.now() < until) {
            timeouts.push(setTimeout(() => findElement(until), 100));
        }
    };

    findElement(Date.now() + FIND_ELEMENT_TIMEOUT);

    return cancel;
};

export default scrollToHash;
